import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { resolveImg } from "@/lib/api";

const FILE_RE = /\.(mp4|webm|mov|m4v)(\?.*)?$/i;
const UID_RE = /^[a-f0-9]{32}$/i;

function withBgParams(url) {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}autoplay=true&muted=true&loop=true&controls=false&preload=auto`;
}

// Background hero video. Supports Cloudflare Stream in all common shapes —
// full iframe/embed URL (incl. customer-<code>.cloudflarestream.com), HLS .m3u8
// manifest URL, or a bare Video UID — plus direct files (.mp4/.webm/.mov) and uploads.
function detect(raw) {
  const src = String(raw).trim();
  // any *.cloudflarestream.com/<uid> or videodelivery.net/<uid> URL
  const cfHost = src.match(/https?:\/\/([^/]*(?:cloudflarestream\.com|videodelivery\.net))\/([a-f0-9]{32})/i);

  // bare UID -> best-effort universal embed (only works on legacy accounts)
  if (UID_RE.test(src)) {
    return { type: "iframe", url: withBgParams(`https://iframe.cloudflarestream.com/${src}`) };
  }

  // full iframe/embed/watch URL (universal OR customer subdomain)
  if (/iframe\.cloudflarestream\.com/i.test(src) ||
      /cloudflarestream\.com\/[a-f0-9]{32}\/iframe/i.test(src) ||
      /\/watch(\b|\/)/i.test(src)) {
    return { type: "iframe", url: withBgParams(src) };
  }

  // HLS manifest
  if (/\.m3u8(\?.*)?$/i.test(src)) {
    const fallbackIframe = cfHost
      ? withBgParams(`https://${cfHost[1]}/${cfHost[2]}/iframe`)
      : null;
    return { type: "hls", url: src, fallbackIframe };
  }

  if (FILE_RE.test(src)) return { type: "file", url: resolveImg(src) };
  if (!src.startsWith("http")) return { type: "file", url: resolveImg(src) };
  return { type: "file", url: src };
}

export function HeroVideo({ src, poster }) {
  const videoRef = useRef(null);
  const [failIframe, setFailIframe] = useState(null);
  const info = src ? detect(src) : null;

  useEffect(() => {
    setFailIframe(null);
    if (!info || info.type !== "hls") return;
    const video = videoRef.current;
    if (!video) return;
    let hls;
    const nativeHls = video.canPlayType("application/vnd.apple.mpegurl");
    if (nativeHls) {
      video.src = info.url;
    } else if (Hls.isSupported()) {
      hls = new Hls({ maxBufferLength: 30, enableWorker: true });
      hls.loadSource(info.url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal && info.fallbackIframe) setFailIframe(info.fallbackIframe);
      });
    } else if (info.fallbackIframe) {
      setFailIframe(info.fallbackIframe);
    }
    return () => { if (hls) hls.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info?.type, info?.url]);

  if (!info) {
    return <img data-testid="hero-media-img" src={poster} alt="Barber craft" className="h-full w-full object-cover" />;
  }

  if (failIframe || info.type === "iframe") {
    return (
      <iframe data-testid="hero-media-iframe" title="Hero video" src={failIframe || info.url}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen className="absolute inset-0 h-full w-full" style={{ border: 0 }} />
    );
  }

  if (info.type === "hls") {
    return <video ref={videoRef} data-testid="hero-media-video" autoPlay muted loop playsInline
      poster={poster} className="h-full w-full object-cover" />;
  }

  return <video data-testid="hero-media-video" src={info.url} autoPlay muted loop playsInline
    poster={poster} className="h-full w-full object-cover" />;
}
